import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "../axios";
import RRWebPlayer from "rrweb-player";
import { getReplayConsolePlugin } from "@rrweb/rrweb-plugin-console-replay";
import "rrweb-player/dist/style.css";
import Draggable from "react-draggable";
import { geFolderById, getPathById } from "../utils";

const MOBILE_MIN_WIDTH = 600;
const CAM_WIDGET_WIDTH = 340;

function isMobile(breakpoint = MOBILE_MIN_WIDTH) {
  const w = window.innerWidth;
  const h = window.innerHeight;
  return Math.min(w, h) < breakpoint;
}
// function isMobile() {
//   return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
//     navigator.userAgent
//   );
// }

const ReplayPlayer = () => {
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const videoRef = useRef(null);
  const { type, uuid, mimeType, id, param5, param6 } = useParams();
  const [camWidgetPosition, setCamWidgetPosition] = useState({ x: 0, y: 0 });
  const lastTimeRef = useRef(0); // Track last time to detect jumps
  const TIME_JUMP_THRESHOLD = 500; // Minimum jump (ms) to detect a click

  let link1 = "";
  let link2 = false;
  if (type === "reading" || type === "listening") {
    link1 = `/test-result/${type}/${param5}`;
  } else if (type === "speaking") {
    link1 = `https://app.ieltsing.com/diplom-speaking-report/${param5}`;
  } else if (type === "writing") {
    link1 = `https://app.ieltsing.com/diplom-writing-report/${param5}`;
    if (param6) {
      link2 = `https://app.ieltsing.com/diplom-writing-report/${param6}`;
    }
  }
  const calculateInitialPosition = () => {
    const rrPlayer = document.querySelector(".rr-player");
    if (rrPlayer) {
      const rrPlayerWidth = rrPlayer.offsetWidth;
      const rrPlayerLeft = rrPlayer.offsetLeft;
      const screenWidth = window.innerWidth;

      if (screenWidth > rrPlayerWidth + CAM_WIDGET_WIDTH) {
        const x = rrPlayerLeft - CAM_WIDGET_WIDTH;

        return { x: x > 0 ? x : 0, y: 0 };
      }
    }
    // Default to left: 0 if condition not met
    return { x: 0, y: 0 };
  };

  useEffect(() => {
    let isMounted = true;

    const handleResize = async () => {
      try {
        // Clean up previous player instance if it exists
        if (playerRef.current) {
          playerRef.current.$destroy?.();
          playerRef.current = null;
        }

        if (videoRef.current) {
          videoRef.current.pause();
          videoRef.current.currentTime = 0;
        }

        // Clear the container
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
        }

        const logsUrl = `${
          process.env.NODE_ENV === "development" ? "http://localhost:5001" : ""
        }/diplom-answers/${geFolderById(id)}/${uuid}.json`;
        const rrwebRes = await axios.get(logsUrl);

        if (rrwebRes.data && isMounted) {
          const events = rrwebRes.data;

          overrideConsoleLogs();

          if (containerRef.current && !playerRef.current) {
            const parsedEvents = events.map((eventString) =>
              JSON.parse(eventString)
            );

            const isLandscape = window.innerWidth > window.innerHeight;
            const width = isLandscape
              ? window.innerWidth / 2
              : window.innerWidth;
            const height = isLandscape
              ? window.innerHeight - 80
              : window.innerHeight / 2 - 80 / 2; // 80px - player height

            playerRef.current = new RRWebPlayer({
              target: containerRef.current,
              props: {
                events: parsedEvents,
                showController: true,
                ...(isMobile() && {
                  width,
                  height,
                }),
                autoPlay: false,
                skipInactive: false,
                plugins: [
                  getReplayConsolePlugin({
                    level: ["info", "log", "warn", "error"],
                  }),
                ],
              },
            });

            const goBlur = () => {
              const iframe = containerRef.current?.querySelector("iframe");
              if (iframe && iframe.contentDocument) {
                const doc = iframe.contentDocument;
                for (let i = 1; i <= 9; i++) {
                  doc.querySelectorAll(`#section${i} img`).forEach((img) => {
                    img.style.setProperty("filter", "blur(10px)", "important");
                  });
                }
              }
            };

            const player = playerRef.current;
            let isPlaying = false;

            // Sync video play/pause state
            player.addEventListener("ui-update-player-state", (event) => {
              const state = event.payload; // "playing" | "paused" | "finished"
              // console.log("STATE ->", state);
              isPlaying = state === "playing";
              if (videoRef.current) {
                switch (state) {
                  case "playing":
                    videoRef.current.play();
                    goBlur();
                    break;
                  case "paused":
                    videoRef.current.pause();
                    break;
                  case "finished":
                    videoRef.current.pause();
                    videoRef.current.currentTime = 0;
                    break;
                  default:
                    // console.log("Unknown state:", state);
                    break;
                }
              }
            });

            player.addEventListener("ui-update-current-time", (e) => {
              const newTime = e.payload; // Assuming payload is time in ms
              const timeDifference = Math.abs(newTime - lastTimeRef.current);

              if (
                videoRef.current &&
                (timeDifference > TIME_JUMP_THRESHOLD || !isPlaying)
              ) {
                goBlur();
                const videoTime = newTime / 1000; // Convert ms to seconds
                videoRef.current.currentTime = videoTime;
              }

              lastTimeRef.current = newTime; // Update last known time
            });
          }
        }
      } catch (e) {
        const wrapper = document.getElementById("playerWrapper");
        if (wrapper) {
          wrapper.style.display = "none";
        }
        console.error("Error initializing player:", e);
      }

      setCamWidgetPosition(calculateInitialPosition());
    };

    window.addEventListener("resize", handleResize);
    if (isMobile()) window.addEventListener("orientationchange", handleResize);
    handleResize();

    return () => {
      window.removeEventListener("resize", handleResize);
      if (isMobile())
        window.removeEventListener("orientationchange", handleResize);

      isMounted = false;
      if (playerRef.current) {
        playerRef.current.$destroy?.();
        playerRef.current = null;
      }
      if (containerRef.current) {
        containerRef.current.innerHTML = "";
      }
    };
  }, [uuid, mimeType, id]);

  const handleDrag = (e, data) => {
    //if (!this.state.isMinimized) {
    setCamWidgetPosition({ x: data.x, y: data.y });
    // }
  };

  const camWidget = (
    <div className="myCamRecorder">
      <video ref={videoRef} playsInline preload="metadata">
        <source
          src={`/diplom-answers/${geFolderById(id)}/${uuid}.${mimeType}`}
          type={`video/${mimeType}`}
        />
        Your browser does not support HTML5 video.
      </video>
    </div>
  );

  return (
    <div className="myContainer">
      {/* {param5 && <a href={`/report/speaking/${param5}`}>Speaking Report</a>} */}

      {isMobile() ? (
        <>{camWidget}</>
      ) : (
        <Draggable
          position={camWidgetPosition}
          onDrag={handleDrag}
          bounds={{
            top: 0,
            left: 0,
            right: window.innerWidth - CAM_WIDGET_WIDTH,
            bottom: window.innerHeight - 265,
          }}
        >
          {camWidget}
        </Draggable>
      )}
      <style jsx>{`
        .myCamRecorder {
          background: rgba(0, 0, 0, 0.63);
          display: inline-block;
          position: fixed;
          z-index: 10000;
          cursor: move;
          border-radius: 10px;
          backdrop-filter: blur(8px);
          box-shadow: 0 0 0 1px rgb(0, 0, 0, 5%), 0 1px 8px rgb(0, 0, 0, 15%);
        }
        .myCamRecorder select,
        .myCamRecorder select:active,
        .myCamRecorder select:focus {
          margin-left: 0;
          margin-right: 0;
          background: #000;
        }

        .myCamRecorder img {
          pointer-events: none;
          -webkit-user-drag: none;
          user-select: none;
        }
        .myCamRecorder video {
          border-radius: 10px;
          max-width: ${CAM_WIDGET_WIDTH}px;
        }

        .video-box {
          width: 250px;
          height: 250px;
          background: rgba(47, 46, 46, 0.9);
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.25);
          border-radius: 8px;
          cursor: move;
          position: fixed;
          z-index: 100000;
          transition: background 0.3s ease-in-out, width 0.3s ease-in-out,
            height 0.3s ease-in-out, transform 0.3s ease-in-out;
        }
        .rr-player__frame {
          background-color: #dbe5f5;
        }
        .rr-progress__handler.svelte-19ke1iv.svelte-19ke1iv,
        .rr-controller__btns.svelte-19ke1iv button.active.svelte-19ke1iv,
        .switch.svelte-9brlez
          input[type="checkbox"].svelte-9brlez:checked
          + label.svelte-9brlez:before {
          background-color: #000 !important;
        }
        .rr-progress__step.svelte-19ke1iv.svelte-19ke1iv {
          background-color: #373737 !important;
        }
        .switch.svelte-9brlez label.svelte-9brlez.svelte-9brlez:before {
          background: rgb(114 114 116 / 50%);
        }
      `}</style>
      <div id="playerWrapper" style={{ textAlign: "center" }}>
        <div ref={containerRef}></div>

        <style jsx>
          {`
            .rr-player {
              float: none;
              margin: 0 auto;
            }
          `}
        </style>

        {isMobile() && (
          <style>{`
          /* 📱 Phone Portrait */
          @media screen and (max-width: ${MOBILE_MIN_WIDTH}px) and (orientation: portrait) {
            .rr-player {
              position: fixed;
              bottom: 0;
              background-color: #eaeef5;
            }
            .rr-timeline {
              width: 100% !important;
            }

            .myContainer {
              display: flex;
              flex-direction: column;
              height: 100vh;
            }
            .myCamRecorder {
              border-radius: 0 !important;
              display: flex;
              flex: 1;
              height: calc(50% - 40px);
              justify-content: center;
              max-width: 100%;
              width: 100%;
            }
            .myCamRecorder video {
              border-radius: 0 !important;
              max-width: 100%;
              width: 100%;
            }
          }

          /* 📱 Phone Landscape */
          @media (orientation: landscape) {
            .rr-player {
              width: 100% !important;
            }
            .rr-timeline {
              width: 100% !important;
            }
            .myCamRecorder {
              right: 0 !important;
              width: calc(50%) !important;
              height: calc(100% - 80px) !important;
              max-width: none !important;
              border-radius: 0 !important;
            }
            .myCamRecorder video {
              width: 100%;
              height: 100%;
              border-radius: 0 !important;
              max-width: none;
            }
          }
        `}</style>
        )}

        <div id="consoleLog"></div>
      </div>
      <div className="hidden pt-3 text-center width-full md:block">
        {param5 && (
          <a
            className="inline-flex items-center justify-center h-10 px-8 mx-2 text-sm font-medium transition-colors rounded-md shadow whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90"
            href={link1}
          >
            Assessment Result
            {param6 ? " 1" : ""}
          </a>
        )}
        {link2 && (
          <a
            className="inline-flex items-center justify-center h-10 px-8 mx-2 text-sm font-medium transition-colors rounded-md shadow whitespace-nowrap focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90"
            href={link2}
          >
            Assessment Result 2
          </a>
        )}
      </div>
    </div>
  );
};

function overrideConsoleLogs() {
  const consoleDiv = document.getElementById("consoleLog");
  if (!consoleDiv) return;

  const methods = ["log", "info", "warn", "error"];

  methods.forEach((method) => {
    const original = console[method];
    console[method] = function (...args) {
      original.apply(console, args);
    };
  });
}

export default ReplayPlayer;
